import {
  type IChart,
  type TChartValueSource,
} from "src/redux/slices/entriesGeneratorSlice";

type Element = string | number;

export const isNumericValue = (val: unknown) =>
  val !== undefined && val !== null && val !== "" && isFinite(Number(val));

export const isChartNumeric = (chart: Pick<IChart, "dataType" | "rawVals">) =>
  chart.dataType
    ? chart.dataType === "numeric"
    : chart.rawVals.some(isNumericValue);

export const resolveValueSource = (
  chart: Pick<IChart, "type" | "valueSource">,
): TChartValueSource => {
  const source =
    chart.valueSource ??
    ({ kind: chart.type === "pie" ? "count" : "entries" } as TChartValueSource);
  return chart.type === "pie" && source.kind === "entries"
    ? { kind: "count" }
    : source;
};
/**
 * Groups elements in an array and counts their occurrences.
 *
 * @param array - An array of elements (strings or numbers) to be grouped and counted.
 * @returns An object containing:
 * - `labels`: An array of unique elements from the input array.
 * - `values`: An array of counts corresponding to each unique element.
 */
export const groupAndCount = (array: Element[]) => {
  const groupedData = array.reduce((acc: Record<Element, number>, item) => {
    if (!acc[item]) {
      acc[item] = 1;
    } else {
      acc[item] += 1;
    }
    return acc;
  }, {});

  const labels = Object.keys(groupedData);
  const values = Object.values(groupedData);

  return { labels, values };
};

export const groupAndSum = (
  labels: Array<Element | undefined>,
  values: Array<Element | undefined> = [],
) => {
  const groupedData: Record<Element, number> = {};

  labels.forEach((label, index) => {
    if (label === undefined || label === "") return;

    if (!isNumericValue(values[index])) return;

    groupedData[label] = (groupedData[label] ?? 0) + Number(values[index]);
  });

  return {
    labels: Object.keys(groupedData),
    values: Object.values(groupedData).map(sum => Math.round(sum * 100) / 100),
  };
};
const PALETTE = [
  ["rgba(255, 99, 132, 1)", "rgba(255, 99, 132, 0.5)"],
  ["rgba(54, 162, 235, 1)", "rgba(54, 162, 235, 0.5)"],
  ["rgba(255, 206, 86, 1)", "rgba(255, 206, 86, 0.5)"],
  ["rgba(75, 192, 192, 1)", "rgba(75, 192, 192, 0.5)"],
  ["rgba(153, 102, 255, 1)", "rgba(153, 102, 255, 0.5)"],
  ["rgba(255, 159, 64, 1)", "rgba(255, 159, 64, 0.5)"],
] as const;

export const getColorsForLabels = (labels: Element[]) => {
  const sortedUnique = [...new Set(labels.map(String))].sort();
  const colorByLabel = new Map(
    sortedUnique.map((label, i) => [label, PALETTE[i % PALETTE.length]]),
  );
  return {
    borderColor: labels.map(l => colorByLabel.get(String(l))![0]),
    backgroundColor: labels.map(l => colorByLabel.get(String(l))![1]),
  };
};

export const sortChartData = (
  labels: Element[],
  values: number[],
  sortBy?: "label" | "value",
  direction: "asc" | "desc" = "asc",
) => {
  if (!sortBy) return { labels, values };

  const indices = labels.map((_, i) => i);
  indices.sort((a, b) => {
    const cmp =
      sortBy === "value"
        ? values[a] - values[b]
        : String(labels[a]).localeCompare(String(labels[b]), undefined, {
            numeric: true,
          });
    return direction === "desc" ? -cmp : cmp;
  });

  return {
    labels: indices.map(i => labels[i]),
    values: indices.map(i => values[i]),
  };
};

/**
 * Calculates a trendline for a given set of numerical values.
 *
 * @param vals - An array of numerical values for which the trendline is calculated.
 * @returns An array of trendline values corresponding to the input values.
 */
export const trendline = (vals: number[]) => {
  const n = vals.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;

  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += vals[i];
    sumXY += i * vals[i];
    sumX2 += i ** 2;
  }

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
  const intercept = (sumY - slope * sumX) / n;

  return Array.from({ length: n }, (_, i) => slope * i + intercept);
};
